import React, { useMemo, useState, useEffect } from 'react'

import { Avatar, Tooltip } from 'antd'
import { familyMembersWithAivardSelector } from 'features/team/model/selectors/teamSelectors'
import { useAppSelector } from 'shared/config/store'
import styled from 'styled-components'
import { marked } from 'marked'
import { AI_BOT_TYPING_TEXT, AI_TYPING_MESSAGE } from 'shared/constants/ai'

interface FormattedMessageProps {
  message: string
  needToParseFromMarkdown?: boolean
  isAIMessage?: boolean
  shouldAnimate?: boolean
  commentId?: string
  onAnimationComplete?: (commentId: string) => void
}

const TypingAnimation: React.FC = () => {
  const [dots, setDots] = useState(1)

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev >= 3 ? 1 : prev + 1)
    }, 500)

    return () => clearInterval(interval)
  }, [])

  return (
    <STypingContainer>
      {AI_BOT_TYPING_TEXT}

      {'.'.repeat(dots)}
    </STypingContainer>
  )
}

const TypewriterText: React.FC<{
  text: string;
  html?: string;
  speed?: number;
  shouldAnimate?: boolean;
  onAnimationComplete?: () => void;
}> = ({
  text,
  html,
  speed = 15,
  shouldAnimate = false,
  onAnimationComplete
}) => {
  const [displayedText, setDisplayedText] = useState('')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const [animationCompleted, setAnimationCompleted] = useState(false)

  // Инициализация состояния при монтировании или изменении shouldAnimate
  useEffect(() => {
    if (shouldAnimate && !animationCompleted) {
      setDisplayedText('')
      setCurrentIndex(0)
      setIsAnimating(true)
    } else if (!shouldAnimate) {
      setDisplayedText(text)
      setCurrentIndex(text.length)
      setIsAnimating(false)
      setAnimationCompleted(false)
    }
  }, [shouldAnimate, text])

  // Основная логика анимации
  useEffect(() => {
    if (!isAnimating || currentIndex >= text.length) {
      if (isAnimating && currentIndex >= text.length && !animationCompleted) {
        setIsAnimating(false)
        setAnimationCompleted(true)
        onAnimationComplete?.()
      }
      return
    }

    const currentChar = text[currentIndex]

    let charDelay = speed
    if (['.', '!', '?'].includes(currentChar)) {
      charDelay = speed * 2
    } else if ([',', ';', ':'].includes(currentChar)) {
      charDelay = speed * 1.5
    } else if (currentChar === '\n') {
      charDelay = speed * 1.5
    }

    charDelay += Math.random() * 5

    const timeout = setTimeout(() => {
      setDisplayedText(prev => prev + currentChar)
      setCurrentIndex(prev => prev + 1)
    }, charDelay)

    return () => clearTimeout(timeout)
  }, [currentIndex, isAnimating, text, speed, onAnimationComplete, animationCompleted])

  const shouldShowHtml = html && (!shouldAnimate || animationCompleted)

  return (
    <STypewriterContainer>
      {shouldShowHtml ? (
        <SHtml dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <span style={{ whiteSpace: 'pre-wrap' }}>
          {displayedText}
        </span>
      )}
    </STypewriterContainer>
  )
}

// Копия типа из TextEditor для использования здесь
type TextPart = {
  type: 'text' | 'mention' | 'link'
  content: string
  start: number
  end: number
  isValid?: boolean
  mentionId?: string
  userData?: any
  url?: string
}


export const FormattedMessage: React.FC<FormattedMessageProps> = ({
  message,
  needToParseFromMarkdown = false,
  isAIMessage = false,
  shouldAnimate = false,
  commentId,
  onAnimationComplete
}) => {
  const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/gi

  const users = useAppSelector(familyMembersWithAivardSelector)

  if (message === AI_TYPING_MESSAGE) {
    return <TypingAnimation />
  }

  if (isAIMessage) {
    const parsedHtml = needToParseFromMarkdown ? marked(message, { async: false }) as string : undefined

    return (
      <TypewriterText
        key={`ai-message-${commentId}-${message.substring(0, 50)}`}
        text={message}
        html={parsedHtml}
        shouldAnimate={shouldAnimate}
        onAnimationComplete={() => onAnimationComplete && onAnimationComplete(commentId)}
      />
    )
  }

  const parseTextToParts = useMemo((): TextPart[] => {
    if (!message) return []

    const sortedUsers = [...users].sort((a, b) => b.name.length - a.name.length)
     interface MentionMatch {
      start: number
      end: number
      content: string
      userId: string
      mentionId: string
      userData?: any
    }

    interface LinkMatch {
      start: number
      end: number
      content: string
      url: string
    }

    const mentions: MentionMatch[] = []
    const links: LinkMatch[] = []

    let linkMatch
    URL_REGEX.lastIndex = 0
    while ((linkMatch = URL_REGEX.exec(message)) !== null) {
      let url = linkMatch[0]
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        if (url.startsWith('www.')) {
          url = 'https://' + url
        } else {
          url = 'https://' + url
        }
      }

      links.push({
        start: linkMatch.index,
        end: linkMatch.index + linkMatch[0].length,
        content: linkMatch[0],
        url: url
      })
    }

    let atIndex = -1
    while ((atIndex = message.indexOf('@', atIndex + 1)) !== -1) {
      const isInsideLink = links.some(link => atIndex >= link.start && atIndex < link.end)
      if (isInsideLink) continue

      for (const user of sortedUsers) {
        const userName = user.name
        const mentionCandidate = message.substring(atIndex + 1)

        if (
          mentionCandidate.toLowerCase().startsWith(userName.toLowerCase()) &&
          ( // После имени должен быть пробел, конец строки или пунктуация
            mentionCandidate.length === userName.length ||
            !(/[a-zA-Zа-яА-Я0-9_]/).test(mentionCandidate[userName.length])
          )
        ) {
          const mentionId = `mention-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
          mentions.push({
            start: atIndex,
            end: atIndex + userName.length + 1,
            content: '@' + userName,
            userId: user.id,
            mentionId,
            userData: user
          })

          break
        }
      }
    }

    const allMatches = [
      ...mentions.map(m => ({ ...m, type: 'mention' as const })),
      ...links.map(l => ({ ...l, type: 'link' as const }))
    ].sort((a, b) => a.start - b.start)

    const parts: TextPart[] = []
    let lastEnd = 0

    for (const match of allMatches) {
      if (match.start > lastEnd) {
        parts.push({
          type: 'text',
          content: message.substring(lastEnd, match.start),
          start: lastEnd,
          end: match.start
        })
      }

      if (match.type === 'mention') {
        parts.push({
          type: 'mention',
          content: match.content,
          start: match.start,
          end: match.end,
          isValid: true,
          mentionId: match.mentionId,
          userData: match.userData
        })
      } else if (match.type === 'link') {
        parts.push({
          type: 'link',
          content: match.content,
          start: match.start,
          end: match.end,
          url: match.url
        })
      }

      lastEnd = match.end
    }

    if (lastEnd < message.length) {
      parts.push({
        type: 'text',
        content: message.substring(lastEnd),
        start: lastEnd,
        end: message.length
      })
    }

    return parts
  }, [message, users])

  const handleLinkClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <SMessageContainer>
      {parseTextToParts.map((part, index) => {
        if (part.type === 'mention') {
          const user = part.userData || users.find(u => u.id === part.userData?.id)

          const tooltipContent = user ? (
            <STooltipContent>
              <Avatar src={user.avatarUrl || undefined}>
                {user.name?.[0] || '?'}
              </Avatar>

              <TooltipText>
                <SText>
                  {user.name}
                </SText>

                {(user as any).phone_or_email && (
                  <EmailText>
                    {(user as any).phone_or_email}
                  </EmailText>
                )}
              </TooltipText>
            </STooltipContent>
          ) : part.content

          return (
            <Tooltip
              color='#141414'
              title={tooltipContent}
              key={`${part.mentionId || index}`}
            >
              <Mention>
                {part.content}
              </Mention>
            </Tooltip>
          )
        }

        if (part.type === 'link') {
          return (
            <SLink
              key={index}
              onClick={() => handleLinkClick(part.url!)}
              title={part.url}
            >
              {part.content}
            </SLink>
          )
        }

        // Обработка обычного текста с переносами строк
        return (
          <>
            {needToParseFromMarkdown && <SFormattedText dangerouslySetInnerHTML={{ __html: marked(part.content, { async: false }) as string }} />}

            {!needToParseFromMarkdown && (
              <STextPart key={index}>
                {part.content}
              </STextPart>
            )}
          </>
        )
      })}
    </SMessageContainer>
  )
}

const EmailText = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
`

const SMessageContainer = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
`

const STextPart = styled.span`
  white-space: pre-wrap;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #FFFFFFD9;
`

const SFormattedText = styled.div`
  white-space: pre-wrap;
  word-break: break-word;

  ul {
    margin: 0;
  }

  p {
    margin: 0;
  }
`

const TooltipText = styled.div`
  display: flex;
  overflow: hidden;
  flex-direction: column;
`

const SText = styled.div`
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100px;
`

const Mention = styled.span`
  color: var(--ant-color-primary-hover);
  border-radius: 4px;
  padding: 1px 3px;
  font-weight: 500;
  cursor: default;
`

const SLink = styled.span`
  color: var(--ant-color-primary-hover);
  cursor: pointer;
  text-decoration: underline;
  word-break: break-all;
  
  &:hover {
    color: #C49AE8;
    text-decoration: underline;
  }
  
  &:active {
    color: #9B6AD6;
  }
`

const STooltipContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
`

const STypingContainer = styled.div`
  font-size: 14px;
  font-weight: 400;
`

const STypewriterContainer = styled.div`
  white-space: pre-wrap;
  word-break: break-word;

  ul {
    margin: 0;
  }

  p {
    margin: 0;
  }
`

const SHtml = styled.div`
  white-space: normal;
  word-break: break-word;

  ul {
    margin: 10px 0;
  }

  p {
    margin: 10px 0;
  }
`
